import { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Icon, type IconName } from '#app/components/ui/icon.tsx'
import { useReveal } from './__use-reveal.ts'

/**
 * The four-step engagement timeline. `n` is the displayed ordinal; the title,
 * duration chip, and body are localized copy from the `public` dictionary.
 */
const STEPS: ReadonlyArray<{
	n: string
	icon: IconName
	key: 'brief' | 'prototype' | 'build' | 'launch'
}> = [
	{ n: '01', icon: 'envelope-closed', key: 'brief' },
	{ n: '02', icon: 'pencil-2', key: 'prototype' },
	{ n: '03', icon: 'laptop', key: 'build' },
	{ n: '04', icon: 'check', key: 'launch' },
]

/**
 * "How it works" — a vertical timeline of the four engagement steps. Each row
 * reveals on scroll via `useReveal`; the connecting line draws (`scaleY`) and
 * the node pops once the row enters view. Motion is progressive enhancement:
 * the `.hiw` rules in `tailwind.css` only bite once `useReveal` adds `.rv`, so
 * SSR / no-JS / reduced-motion visitors get the full resting state. Tokens only.
 */
export function HowItWorks() {
	const { t } = useTranslation('public')
	const ref = useRef<HTMLElement>(null)
	useReveal(ref)

	return (
		<section
			id="how-it-works"
			ref={ref}
			aria-labelledby="how-it-works-heading"
			className="container scroll-mt-20 py-32"
		>
			<div className="mx-auto max-w-2xl text-center">
				<p className="text-brand text-body-xs font-semibold tracking-eyebrow uppercase">
					{t('landing.howItWorks.eyebrow')}
				</p>
				<h2
					id="how-it-works-heading"
					className="mt-4 text-3xl font-semibold tracking-h2 text-balance sm:text-4xl"
				>
					{t('landing.howItWorks.heading')}
				</h2>
				<p className="text-muted-foreground mt-5 text-lg text-pretty">
					{t('landing.howItWorks.lead')}
				</p>
			</div>

			<ol role="list" className="hiw mx-auto mt-20 max-w-2xl">
				{STEPS.map((step, index) => (
					<li
						key={step.n}
						data-reveal
						style={{ transitionDelay: `${index * 90}ms` }}
						className="grid grid-cols-[3.5rem_1fr] gap-6 sm:gap-8"
					>
						<div className="flex flex-col items-center">
							<span className="hiw-node bg-brand-soft text-brand border-border grid h-14 w-14 place-items-center rounded-xl border">
								<Icon name={step.icon} className="h-6 w-6" aria-hidden />
							</span>
							{index < STEPS.length - 1 ? (
								<span
									aria-hidden
									className="hiw-line bg-border my-3 w-px flex-1"
								/>
							) : null}
						</div>
						<div className={index < STEPS.length - 1 ? 'pb-24' : ''}>
							<div className="mb-3 flex flex-wrap items-baseline gap-3">
								<span className="text-brand text-body-2xs font-bold tracking-eyebrow">
									{step.n}
								</span>
								<h3 className="text-body-md font-semibold tracking-h2">
									{t(`landing.howItWorks.steps.${step.key}.title`)}
								</h3>
								<span className="text-brand bg-brand-soft border-border ml-auto rounded-full border px-2.5 py-1 text-body-2xs font-medium tracking-eyebrow whitespace-nowrap">
									{t(`landing.howItWorks.steps.${step.key}.dur`)}
								</span>
							</div>
							<p className="text-muted-foreground text-[15px] leading-relaxed text-pretty">
								{t(`landing.howItWorks.steps.${step.key}.body`)}
							</p>
						</div>
					</li>
				))}
			</ol>
		</section>
	)
}
